import {
  MinusCircleOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Space,
  Table,
  Typography,
  TimePicker,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useEffect } from "react";
import { FormProps } from "../FormProps";
import TourOverlay from "../../../Components/Tour/TourOverlay";

dayjs.extend(utc);
dayjs.extend(timezone);

interface DataType {
  key: string;
  date: string;
  fromTime: string;
  toTime: string;
}

const UnavailabilityForm: React.FC<FormProps> = ({
  settings,
  updateSettings,
  timeZone,
}) => {
  const [tableData, setTableData] = useState<DataType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newSlot, setNewSlot] = useState({
    date: null as dayjs.Dayjs | null,
    fromTime: null as dayjs.Dayjs | null,
    toTime: null as dayjs.Dayjs | null,
  });

  useEffect(() => {
    if (settings) {
      const unavailableSlots = settings.unavailableSlots || [];
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const updatedTableData = unavailableSlots
        ?.filter((slot: any) => {
          const slotDate = new Date(slot.date);
          slotDate.setHours(0, 0, 0, 0);
          return slotDate >= currentDate;
        })
        ?.map((slot: any, index: number) => ({
          key: index.toString(),
          date: new Date(slot.date).toISOString().split("T")[0],
          // fromTime: formatTime(new Date(slot.fromTime)),
          // toTime: formatTime(new Date(slot.toTime)),
          fromTime: dayjs(slot.fromTime).tz(timeZone).format("h:mm A"),
          toTime: dayjs(slot.toTime).tz(timeZone).format("h:mm A"),
        }));

      setTableData(updatedTableData);
    } else {
      setTableData([]);
    }
  }, [settings, timeZone]);

  const handleDelete = async (key: string) => {
    if (!settings) return;

    const newDataSource = tableData.filter((item) => item.key !== key);
    setTableData(newDataSource);

    const updatedUnavailableSlots = settings.unavailableSlots.filter(
      (_: any, index: number) => index.toString() !== key
    );

    settings.unavailableSlots = updatedUnavailableSlots;
    await updateSettings(settings, false, false);
  };

  const handleAdd = async () => {
    const { date, fromTime, toTime } = newSlot;

    if (date && fromTime && toTime && settings) {
      const newSlotObj = {
        date: new Date(date.format("YYYY-MM-DD")),
        // fromTime: new Date(
        //   dayjs(date)
        //     .hour(fromTime.hour())
        //     .minute(fromTime.minute())
        //     .second(0)
        //     .format("YYYY-MM-DD HH:mm:ss")
        // ),
        // toTime: new Date(
        //   dayjs(date)
        //     .hour(toTime.hour())
        //     .minute(toTime.minute())
        //     .second(0)
        //     .format("YYYY-MM-DD HH:mm:ss")
        // ),
        fromTime: dayjs
          .tz(
            `${date.format("YYYY-MM-DD")} ${fromTime.format("HH:mm:ss")}`,
            "YYYY-MM-DD HH:mm:ss",
            timeZone
          )
          .utc()
          .toISOString(),
        toTime: dayjs
          .tz(
            `${date.format("YYYY-MM-DD")} ${toTime.format("HH:mm:ss")}`,
            "YYYY-MM-DD HH:mm:ss",
            timeZone
          )
          .utc()
          .toISOString(),
      };
      console.log("🚀 ~ handleAdd ~ newSlotObj:", newSlotObj);

      settings.unavailableSlots.push(newSlotObj);
      await updateSettings(settings, false, true);

      setTableData((prevTableData) => [
        ...prevTableData,
        {
          key: (prevTableData?.length + 1).toString(),
          date: date.format("YYYY-MM-DD"),
          fromTime: dayjs(fromTime).tz(timeZone).format("h:mm A"),
          toTime: dayjs(toTime).tz(timeZone).format("h:mm A"),
        },
      ]);

      setNewSlot({
        date: null,
        fromTime: null,
        toTime: null,
      });
      setError(null);
    }
  };

  const handleChangeDate = (date: dayjs.Dayjs | null) => {
    const today = dayjs().startOf("day");
    if (date && date.isBefore(today)) {
      setError("Selected date cannot be in the past.");
      return;
    }

    setError(null);

    if (!error) {
      setNewSlot((prev) => ({
        ...prev,
        date,
      }));
    }
  };

  const handleChangeFromTime = (time: dayjs.Dayjs | null) => {
    setNewSlot((prev) => ({
      ...prev,
      fromTime: time,
    }));
  };

  const handleChangeToTime = (time: dayjs.Dayjs | null) => {
    if (newSlot.fromTime && time) {
      if (time.isBefore(newSlot.fromTime) || time.isSame(newSlot.fromTime)) {
        setError("To time must be after from time.");
        return;
      }
    }
    setError(null);
    if (!error) {
      setNewSlot((prev) => ({
        ...prev,
        toTime: time,
      }));
    }
  };

  const markEntireDayAsUnavailable = async () => {
    const { date } = newSlot;

    if (date && settings) {
      const newSlotObj = {
        date: new Date(date.format("YYYY-MM-DD")),
        fromTime: dayjs
          .tz(date.startOf("day").format("YYYY-MM-DD HH:mm:ss"), timeZone)
          .utc()
          .toISOString(),
        toTime: dayjs
          .tz(date.endOf("day").format("YYYY-MM-DD HH:mm:ss"), timeZone)
          .utc()
          .toISOString(),
      };

      settings.unavailableSlots.push(newSlotObj);
      await updateSettings(settings, false, true);

      setTableData((prevTableData) => [
        ...prevTableData,
        {
          key: (prevTableData?.length + 1).toString(),
          date: date.format("YYYY-MM-DD"),
          fromTime: "12:00 AM",
          toTime: "11:59 PM",
        },
      ]);

      setNewSlot({
        date: null,
        fromTime: null,
        toTime: null,
      });
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text: string) => <span>{dayjs(text).format("DD/MM/YYYY")}</span>,
    },
    {
      title: "From Time",
      dataIndex: "fromTime",
      key: "fromTime",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "To Time",
      dataIndex: "toTime",
      key: "toTime",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Remove",
      key: "action",
      render: (_: any, record: DataType) => (
        <Space size="middle">
          <Button
            type="dashed"
            icon={<MinusCircleOutlined />}
            onClick={() => handleDelete(record.key)}
          />
        </Space>
      ),
    },
  ];

  return (
    <TourOverlay targetId="unavailability-section" className="unavailability-section">
      <Space direction="vertical" size="large">
        <Typography.Title level={5} style={{ marginTop: 3 }}>
          Unavailability
        </Typography.Title>
        <div>
          <Space direction="vertical" size="small">
            <Space size="small">
              <DatePicker
                placeholder="Date"
                format="DD/MM/YYYY"
                picker="date"
                onChange={handleChangeDate}
                value={newSlot.date ? newSlot.date : undefined}
                disabledDate={(current) => {
                  return current && current < dayjs().startOf("day");
                }}
              />
              <TimePicker
                placeholder="From Time"
                format="h:mm A"
                minuteStep={15}
                use12Hours
                onChange={handleChangeFromTime}
                value={newSlot.fromTime ? newSlot.fromTime : undefined}
                needConfirm={false}
                showNow={false}
              />
              <TimePicker
                placeholder="To Time"
                format="h:mm A"
                minuteStep={15}
                use12Hours
                onChange={handleChangeToTime}
                value={newSlot.toTime ? newSlot.toTime : undefined}
                needConfirm={false}
                showNow={false}
              />
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              />
              <Tooltip title={"Mark entire day as unavailable"}>
                <Button
                  type="dashed"
                  icon={<StopOutlined />}
                  onClick={markEntireDayAsUnavailable}
                />
              </Tooltip>
            </Space>
            {error && <Typography.Text type="danger">{error}</Typography.Text>}
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          locale={{
            emptyText: settings
              ? "There are no unavailable slots"
              : "Settings not available",
          }}
        />
      </Space>
    </TourOverlay>
  );
};

export default UnavailabilityForm;
